// =============================================================================
// Copyright   : 2020 semantics GmbH
// Date        : $Date: 2025-01-29 17:15:31 +0100 (Wed, 29 Jan 2025) $
// Author      : Ulrich Mierendorff, Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 149644 $
// Purpose     : Database Administration and Debugging Parts
// =============================================================================
// ---Third-party imports
import React, {useState} from 'react'
import {
  Icon,
  Header,
  Table,
  Menu,
  Dropdown,
  List,
  Message,
  Button,
  Loader,
  Divider,
  Breadcrumb,
  Segment,
} from 'semantic-ui-react'

import {formatDistance} from 'date-fns'

// ---Framework imports
import log from 'uix/logging'
import {sum, values, mapItems, keys, strip, lstrip, uniq} from 'ldash'
import {
  HeaderSegment,
  XMenu,
  TranslatedTable,
  ApiErrorMessage,
  SplitPane,
  TableSegment,
  XPopup,
  SelectableTable,
  Editor,
} from 'uix/components'
import {useActions, useApi, useMemo, usePersistent, useCallApi} from 'uix/hooks'
import {prettyBytes, formatDate} from 'uix/utils'
import {DmpViewBasic} from 'uix/chunks'
import {usePersistentToggle} from 'uix/components/menu'

// ---Local imports
import {ACTIVITIES_MAX_RESULTS_DEFAULT, ACTIVITIES_MIN_DURATION_DEFAULT} from './constants'

// ---Globals

const databaseIcon = subicon => ['database', {name: subicon, color: 'blue'}]
export const DatabaseCommand = props => {
  let {callApi: getDatabaseQueryResult, calling} = useCallApi('getDatabaseQueryResult', {
    timeout: Infinity,
    disableErrorNotify: true,
  })

  let [limit, setLimit] = useState('100')

  let [sql_input, set_sql_input] = usePersistent('sql_cmd', '')
  let [res, set_res] = useState({})

  let [db_commit, set_db_commit] = useState(false)

  let columns = useMemo(() => {
    if (!res.error && res.data?.result_table) {
      return mapItems(res.data.result_table[0], colname => ({
        Header: colname,
        accessor: colname,
      }))
    } else {
      return null
    }
  }, [res])

  let subheader = 'Run a query (CTRL+ENTER)'

  let output = useMemo(() => {
    if (res.error) {
      return <ApiErrorMessage error={res.error} traceback />
    } else if (res.data) {
      if (res.data.affected !== false) {
        return (
          <Message positive>
            <Message.Header>{res.data.affected} rows affected.</Message.Header>
            <p>{res.data.params.commit ? <>Commited</> : <>But changes NOT COMMITED.</>}</p>
          </Message>
        )
      } else {
        // TheadComponent+Tbodycomponent inspired by https://codesandbox.io/s/oo9j4z1139
        let TheadComponent = props => {
          return (
            <div
              className="rt-thead -headerGroups"
              style={{minWidth: 'auto !important', overflowX: 'hidden'}}
            >
              {props.children}
            </div>
          )
        }

        let TbodyComponent = props => {
          let handleScroll = e => {
            let headers = e.target.parentElement.getElementsByClassName('rt-thead')
            for (let i = 0; i < headers.length; i++) {
              headers[i].scrollLeft = event.target.scrollLeft
            }
          }
          return (
            <div className="rt-tbody" style={{minWidth: 'auto !important'}} onScroll={handleScroll}>
              {props.children}
            </div>
          )
        }
        subheader = `${res.data.result_table.length} records selected`

        return (
          <>
            <TranslatedTable
              compact
              data={res.data.result_table}
              columns={columns}
              className="-striped -highlight dynHeight"
              showPagination={false}
              pageSize={res.data.result_table.length}
              TheadComponent={TheadComponent}
              TbodyComponent={TbodyComponent}
            />
          </>
        )
      }
    } else if (calling) {
      return <Loader active />
    } else {
      return null
    }
  }, [res, calling])

  let handleSubmit = () => {
    getDatabaseQueryResult({query: sql_input, commit: db_commit, limit}, (data, error) => {
      set_res({data, error})
    })
    set_db_commit(false)
  }
  let db_menu = (
    <XMenu>
      <XMenu.Input value={limit} onValueChange={setLimit} label="Limit" width={60} />
      <Menu.Item icon="play" content="Execute" onClick={handleSubmit} disabled={calling} />
      <Menu.Item
        content="Commit"
        icon={db_commit ? 'check square outline' : 'square outline'}
        active={db_commit}
        onClick={() => set_db_commit(!db_commit)}
      />
    </XMenu>
  )
  return (
    <HeaderSegment
      major
      flex
      header="Database"
      subheader={subheader}
      menu={db_menu}
      icon={databaseIcon('play')}
      flexInner
    >
      <SplitPane flex flexInner>
        <Editor
          flex
          text={sql_input}
          onBeforeChange={({value}) => set_sql_input(value)}
          extraKeys={{'Ctrl-Enter': handleSubmit}}
          opts={{mode: 'sql'}}
        />

        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'row',
            overflow: 'auto',
          }}
        >
          {output}
        </div>
      </SplitPane>
    </HeaderSegment>
  )
}

export const DatabaseTableStats = props => {
  let {callApi: refresh, calling} = useCallApi('getDatabaseTableStats')

  let {data: stats_res, error: stats_res_error, reload, loading} = useApi(
    'getDatabaseTableStats',
    {autoreset: false},
    {timeout: Infinity}
  )

  let output = null
  if (stats_res_error) {
    output = <ApiErrorMessage error={stats_res_error} traceback />
  } else if (stats_res) {
    let _basic_stats = (
      <List horizontal relaxed>
        <List.Item>
          <List.Header>Last Update</List.Header>
          {stats_res.update}
        </List.Item>
        <List.Item>
          <List.Header>No. of Tables</List.Header>
          {stats_res.tables.length}
        </List.Item>
        <List.Item>
          <List.Header>Total Size</List.Header>
          {prettyBytes(sum(stats_res.tables.map(stats => parseFloat(stats.sizeInMB))) * 1024 ** 2)}
        </List.Item>
      </List>
    )
    let _columns = [
      {Header: 'Name', accessor: 'name'},
      {
        Header: 'Records',
        accessor: 'numRecords',
        minWidth: 75,
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: 'AvgRec [B]',
        accessor: 'avgRecordSizeKB',
        minWidth: 75,
        style: {
          textAlign: 'right',
        },
        Cell: props => <>{parseInt(parseFloat(props.value) * 1024)}</>,
      },
      {
        Header: 'Pages',
        accessor: 'countPages',
        minWidth: 60,
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: 'Size [MB]',
        accessor: 'sizeInMB',
        minWidth: 75,
        style: {
          textAlign: 'right',
        },
        Cell: props => <>{parseFloat(props.value).toFixed(2)}</>,
      },
      /*Size (%)*/
      {
        Header: 'avg Fill',
        accessor: 'avgFill',
        minWidth: 60,
        style: {
          textAlign: 'right',
        },
        Cell: props => <>{parseFloat(props.value).toFixed(1)} %</>,
      },
      {
        Header: '0-19%',
        accessor: 'fill0',
        minWidth: 60,
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: '20-39%',
        accessor: 'fill1',
        minWidth: 60,
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: '40-59%',
        accessor: 'fill2',
        minWidth: 60,
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: '60-79%',
        accessor: 'fill3',
        minWidth: 60,
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: '80-99%',
        accessor: 'fill4',
        minWidth: 60,
        style: {
          textAlign: 'right',
        },
      },
    ]
    let _default_sort = [
      {
        id: 'sizeInMB',
        desc: true,
      },
      {
        id: 'name',
      },
    ]
    output = (
      <Segment raised>
        <div>{_basic_stats}</div>
        <Divider hidden />
        <div>
          <TranslatedTable
            compact
            data={stats_res.tables}
            columns={_columns}
            className="-striped -highlight dynHeight"
            showPagination={false}
            pageSize={stats_res.tables.length}
            defaultSorted={_default_sort}
          />
        </div>
      </Segment>
    )
  }

  let menu = (
    <XMenu>
      <Menu.Item icon="refresh" onClick={() => refresh({refresh: true}, reload)} />
    </XMenu>
  )
  return (
    <HeaderSegment
      major
      header="Table Statistics"
      subheader="For OPR_*, SYS_*, PRV_*"
      menu={menu}
      icon={databaseIcon('sort amount down')}
      loading={loading || calling}
    >
      {output}
    </HeaderSegment>
  )
}

export const DatabaseIndexes = ({databaseIndexesData: data, callApi, reloadPartFilters}) => {
  const IDX_WARN_LEVEL = 0.95
  let [hide_empty, set_hide_empty] = useState(true)
  let [bg_updating, set_bg_updating] = useState(false)

  let refreshIndexes = () => {
    callApi('databaseUpdateIndexStatistics', () => {
      set_bg_updating(false)
      reloadPartFilters('DatabaseIndexes')
    })
    set_bg_updating(true)
  }

  let TableIndexInfo = (tname, tdata) => {
    // Renders the index information for a single database table
    let subheader = `~ ${tdata.row_cnt_est} rows`
    if (tdata.row_cnt_est == null) {
      subheader = 'no row count estimate'
    }
    let rows = tdata.indexes.map(idx_data => (
      <Table.Row key={idx_data.name} disabled={Boolean(idx_data.inactive)}>
        <Table.Cell>
          {idx_data.name} {idx_data.inactive ? 'inactive' : ''}
        </Table.Cell>
        <Table.Cell>{idx_data.fk}</Table.Cell>
        <Table.Cell
          textAlign="right"
          warning={idx_data.stat > IDX_WARN_LEVEL}
          error={idx_data.stat < 0}
        >
          {!(0 <= idx_data.stat && idx_data.stat <= IDX_WARN_LEVEL) ? (
            <Icon name="attention" />
          ) : (
            ''
          )}
          1:{Math.floor(parseFloat(idx_data.stat) ** -1)}
        </Table.Cell>
      </Table.Row>
    ))

    return (
      <Segment style={{padding: '0'}} raised key={tname}>
        <Header as="h5" attached="top">
          {`${tname} [${subheader}]`}
        </Header>
        <Table attached="bottom" compact="very" fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Index Name</Table.HeaderCell>
              <Table.HeaderCell>→ Foreign Key</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Selectivity</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>{rows}</Table.Body>
        </Table>
      </Segment>
    )
  }
  let output = mapItems(data, TableIndexInfo, (name, data) =>
    hide_empty ? data.row_cnt_est : true
  )
  let menu = (
    <XMenu>
      <Menu.Item
        icon={{name: 'refresh', loading: bg_updating}}
        onClick={refreshIndexes}
        disabled={bg_updating}
        title="Update index statistics"
        content="Update Statistics"
      />
      <Menu.Item
        active={!hide_empty}
        content="Show Empty Tables"
        onClick={() => set_hide_empty(!hide_empty)}
      />
    </XMenu>
  )
  return (
    <HeaderSegment
      major
      header="Index Statistics"
      subheader="Separated by Table"
      menu={menu}
      icon={databaseIcon('search')}
    >
      {output}
    </HeaderSegment>
  )
}

export const DatabaseActivities = ({databaseOperations: data, reloadPartFilters, go, request}) => {
  const MAX_PAGE_SIZE = 200
  let [showSelect, showSelectToggle] = usePersistentToggle('showSelect', {
    initialValue: true,
    content: 'select',
    icon: 'filter',
  })

  let dedent = s => {
    let lines = s.split('\n')
    let indents = lines.map(l => l.length - lstrip(l).length).filter(e => e > 1)
    if (indents.length == 0) return s
    let min_indent = Math.min(...indents)
    let ws = ' '.repeat(min_indent)
    lines = lines.map(l => (l.startsWith(ws) ? l.slice(min_indent) : l))
    return strip(lines.join('\n'))
  }

  data = useMemo(() => {
    let res = data
    if (res) {
      res = res.map(e => ({...e, query: dedent(e.query)}))
    }
    if (res && !showSelect) {
      res = res.filter(
        e =>
          !strip(e.query)
            .toLowerCase()
            .startsWith('select')
      )
    }
    return res
  }, [data, showSelect])

  // no useState() here, because the query/URL is the "single source of truth"!
  let min_duration = request.query.minDuration || ACTIVITIES_MIN_DURATION_DEFAULT
  let max_results = request.query.max || ACTIVITIES_MAX_RESULTS_DEFAULT

  let [show_plan, set_show_plan] = useState(false)

  let min_duration_selection = [
    {
      // note: depeding on backend implementation, does not really show everything
      text: 'Show All Durations',
      value: 0,
    },
    {
      text: '>= 100 ms',
      value: 100,
    },
    {
      text: '>= 1 sec',
      value: 1000,
    },
    {
      text: '>= 10 sec',
      value: 10000,
    },
  ]
  let max_results_selection = [
    {text: '10', value: 10},
    {text: '100', value: 100},
    {text: '1000', value: 1000},
    {text: '5000', value: 5000},
  ]
  let onChangeConfig = (param, default_value) => {
    return (e, {value}) => {
      if (value != default_value) {
        go({updateQuery: {[param]: value}})
      } else {
        go({updateQuery: {[param]: null}})
      }
    }
  }
  let handleRefresh = () => {
    reloadPartFilters('DatabaseActivities')
  }

  let _cell_details_onclick = ({value, aggregated}) =>
    aggregated ? (
      <span>uniqe: {value.length}</span>
    ) : (
      <XPopup
        trigger={<span>{value}</span>}
        content={<pre style={{margin: 0}}>{value}</pre>}
        on="click"
        inverted
        basic
        compact
      />
    )
  let tcolumns = useMemo(
    () => [
      {
        Header: 'Time',
        accessor: 'ts',
        maxWidth: 100,
        Cell: ({value, aggregated}) => (aggregated ? null : formatDate(value, 'HH:MM:ss.SSS')),
        aggregate: () => null,
      },
      {
        Header: 'Context',
        accessor: 'context',
        minWidth: 75,
        groupable: true,
      },
      {
        Header: 'Query (click for full)',
        accessor: 'query',
        Cell: _cell_details_onclick,
        aggregate: vals => uniq(vals),
      },
      {
        Header: 'Plan (click for full)',
        accessor: 'plan',
        Cell: _cell_details_onclick,
        show: show_plan,
        aggregate: vals => uniq(vals),
      },

      {
        Header: 'Prep [ms]',
        accessor: 'preptime',
        minWidth: 25,
        style: {
          textAlign: 'right',
        },
        Cell: props => (props.value == -1 ? 'prepared' : props.value),
        aggregate: vals => sum(vals),
      },
      {
        Header: 'Exec [ms]',
        accessor: 'exectime',
        minWidth: 25,
        style: {
          textAlign: 'right',
        },
        aggregate: vals => sum(vals),
      },
    ],
    [show_plan]
  )
  let tdefault_sort = [
    {
      id: 'ts', // even though date is string, works, because it is padded and formated ISO-like
      desc: true,
    },
  ]
  let menu = (
    <XMenu>
      {showSelectToggle}
      <XMenu.Item
        active={show_plan}
        content="Show Plan"
        onClick={() => set_show_plan(!show_plan)}
      />
      <XMenu.Dropdown
        header="Filter Slow Queries"
        options={min_duration_selection}
        value={min_duration}
        onChange={onChangeConfig('minDuration', ACTIVITIES_MIN_DURATION_DEFAULT)}
      />
      <XMenu.Dropdown
        header="Limit Results Size"
        options={max_results_selection}
        value={max_results}
        onChange={onChangeConfig('max', ACTIVITIES_MAX_RESULTS_DEFAULT)}
      />
      <XMenu.Item icon="refresh" onClick={handleRefresh}></XMenu.Item>
    </XMenu>
  )
  return (
    <TableSegment
      major
      header="Activities"
      subheader="Last Operations on the Database"
      menu={menu}
      icon={databaseIcon('lightning')}
      fullscreenable
    >
      <TranslatedTable
        compact
        data={data}
        columns={tcolumns}
        showPagination
        showCount
        defaultSorted={tdefault_sort}
        filterable
        defaultFilterMethod={(filter, row) =>
          String(row[filter.id])
            .toLowerCase()
            .includes(filter.value)
        }
      />
    </TableSegment>
  )
}

export const DatabaseSchema = ({databaseSchemaData: data, callApi, reloadPartFilters}) => {
  // XXX: speed up rendering (takes several seconds)
  let [refdb_updating, set_refdb_updating] = useState(false)
  let updateRefDb = () => {
    set_refdb_updating(true)
    callApi(
      'updateSchemaReferenceDb',
      () => {
        set_refdb_updating(false)
        reloadPartFilters('DatabaseSchema')
      },
      {timeout: Infinity}
    )
  }

  let [desc_updating, set_desc_updating] = useState(false)
  let updateDescriptions = () => {
    set_desc_updating(true)
    callApi('updateDatabaseSchemaDescriptions', () => {
      set_desc_updating(false)
      reloadPartFilters('DatabaseSchema')
    })
  }

  let render_tcell = val => {
    /* renders a cell in a table listing all sub-items of an item
      (e.g. overview of all fields in a table) */
    if (typeof val === 'boolean') {
      return val ? <Icon name="checkmark" /> : 'NO'
    } else if (typeof val === 'string') {
      // we do not use <pre> here to use space in table cells better, and also,
      // wrapping pre in table cells works just "ok"
      // we do not use .trim() but .replace() to be able to display trailing whitespace
      return val
        .replace(/(^\n+)|(\n+$)/g, '')
        .split('\n')
        .map((para, idx) => (
          <p key={idx} style={{margin: 0}}>
            {para.replace(/^\s/g, '\xa0')}
          </p>
        ))
    }
    return val
  }

  // (any) error messsages get an ID that is unique for the whole document to jump
  // back and forth between errors.
  let err_msg_uid_cnt = 0
  let render_item_hierarchy = (path, item) => {
    /* Recursive rendering of data-object
      - path (list) is the current path in the data hierarchy / Breadcrumbs
        (e.g. [Schema]  or  [Schema, Tables]  or  [Schema, Tables, mytable]   and so on)
      - item is a subobjct from the global data object
        (e.g. data or data.items.Tables or data.items.Tables.items.mytable   and so on)

      In this method the following other names are used
        - subitem: subobject of item (item.items.X)

      At the current lvl. of recursion, the following output is rendered for our item:
        - a header
        - optionally some (error-)messages
        - optional details
        - optional table of subitems

      Depending on various factors (see func do_subrender), a recursion is
      performed afterwards to render more information for subitems.
    */

    /* Determines whether subrendering will be performed for a subitem. This is necessary if:
        - a subitem has subitems (subitem.items) itself
        - a subitem has a details text (does not appear in table overview because of its size)
        - a subitem has >0 errors. Note: It is possible that the subitem itself has no error MESSAGE
          attached to it, but other sub-sub-items further down the hierarchy may have messages, so we
          have to check the error_cnt here instead of errors-field.
     */
    let do_subrender = subitem =>
      subitem.items || subitem.details || subitem.error_cnt ? true : false

    // error messages and details text
    let messages = []
    if (item.errors.length > 0) {
      messages = messages.concat(
        item.errors.map((err, idx) => {
          let err_msg_diffview = null
          let current_val = err.subpath.reduce((obj, key) => obj[key], item)
          let orig_val = err.orig
          if (typeof current_val !== 'object' && typeof orig_val !== 'object') {
            if (typeof current_val === 'string' || typeof orig_val === 'string') {
              current_val = (current_val ?? '').toString()
              orig_val = (orig_val ?? '').toString()
              // we do not use codemirror merge-view, because a simple dmp view is much faster
              // when displayed multiple/many times on the same page
              // <DiffView options={{value: current_val, origRight: orig_val, allowEditingOriginals: false}} height={100}>{orig_val}</DiffView>
              err_msg_diffview = (
                <>
                  <p>Difference between original/schema and current value:</p>
                  <DmpViewBasic cleanup current={current_val} orig={orig_val} />
                </>
              )
            } else {
              err_msg_diffview = (
                <>
                  <p>Original/Schema Value: {JSON.stringify(orig_val)}</p>
                  <p>Current Value: {JSON.stringify(current_val)}</p>
                </>
              )
            }
          }

          let err_msg_subpath_info = null
          if (err.subpath[0] == 'meta') {
            err_msg_subpath_info = <> ({err.subpath.slice(1).join(' → ')})</>
          }
          let err_msg_jumpicons = (
            <span style={{float: 'right'}}>
              <a href={`#error-msg-${err_msg_uid_cnt - 1}`} title="Previous Error">
                <Icon name="arrow alternate circle up outline" disabled={err_msg_uid_cnt == 0} />
              </a>
              <a href={`#error-msg-${err_msg_uid_cnt}`} title="This Error">
                <Icon name="share" />
              </a>
              <a href={`#error-msg-${err_msg_uid_cnt + 1}`}>
                <Icon name="arrow alternate circle down outline" title="Next Error" />
              </a>
            </span>
          )
          let full_message = (
            <Message
              attached
              size="small"
              key={`error${idx}`}
              id={`error-msg-${err_msg_uid_cnt}`}
              error
            >
              <Message.Header>
                <Icon name="warning circle" />
                {err.msg}
                {err_msg_subpath_info}
                {err_msg_jumpicons}
              </Message.Header>
              {err_msg_diffview}
            </Message>
          )
          err_msg_uid_cnt += 1
          return full_message
        })
      )
    }
    if (item.details) {
      messages.push(
        <Message attached size="small" key="details">
          <pre style={{whiteSpace: 'pre-wrap'}}>{item.details.trim()}</pre>
        </Message>
      )
    }

    let table = null
    let subrender = null
    if (item.items) {
      // here, we know, that every subitem has the same meta-"columns" (if any)
      let meta_headers = keys(values(item.items)[0].meta || {})
      let thead = (
        <Table.Row>
          <Table.HeaderCell width={1} textAlign="right"></Table.HeaderCell>
          <Table.HeaderCell width={4}>Name</Table.HeaderCell>
          {meta_headers.map(name => (
            <Table.HeaderCell key={name}>{name}</Table.HeaderCell>
          ))}
          <Table.HeaderCell width={meta_headers.length ? 1 : null} textAlign="right">
            Errors
          </Table.HeaderCell>
        </Table.Row>
      )
      let tbody = mapItems(item.items, (name, subitem, idx) => {
        let name_col = null
        if (do_subrender(subitem)) {
          name_col = <a href={'#' + path.join(',') + ',' + name}>{name}</a>
        } else {
          name_col = name
        }
        return (
          <Table.Row error={subitem.error_cnt > 0} key={name}>
            <Table.Cell textAlign="right">{idx + 1}</Table.Cell>
            <Table.Cell>{name_col}</Table.Cell>
            {meta_headers.map(name => (
              <Table.Cell key={name}>{render_tcell(subitem.meta[name])}</Table.Cell>
            ))}
            <Table.Cell textAlign="right">{subitem.error_cnt}</Table.Cell>
          </Table.Row>
        )
      })
      table = (
        <Table attached="bottom" compact="very" selectable>
          <Table.Header>{thead}</Table.Header>
          <Table.Body>{tbody}</Table.Body>
        </Table>
      )
      subrender = mapItems(
        item.items,
        (name, subitem) => render_item_hierarchy([...path, name], subitem),
        (name, subitem) => do_subrender(subitem)
      )
    }
    // returning a list here to prevent nested HTML structure
    return [
      // we need <div> here instead of <> for correct formatting of elements (why?)
      <HeaderSegment
        basic
        header={
          <>
            <Breadcrumb
              icon="right angle"
              sections={path.map((text, idx, arr) => {
                let islast = idx == arr.length - 1
                let content = text
                if (!islast) {
                  content = <a href={'#' + path.slice(0, idx + 1).join(',')}>{text}</a>
                }
                return {
                  key: text,
                  content: content,
                  active: islast,
                }
              })}
            />
            <Header.Subheader style={{float: 'right'}}></Header.Subheader>
          </>
        }
        subheader={`${keys(item.items ?? {}).length} Items`}
        key={path}
        id={path}
      >
        {messages}
        {table}
      </HeaderSegment>,
      subrender,
    ]
  }

  let schema_output = render_item_hierarchy(['Schema'], data)

  let welcome_message = null
  let tail_message = null
  if (err_msg_uid_cnt > 0) {
    welcome_message = (
      <Message error>
        <Message.Header>
          There are {err_msg_uid_cnt} errors in your database (differences to reference schema).
        </Message.Header>
        <a href="#error-msg-0">
          Jump to First
          <Icon name="arrow alternate circle down outline" />
        </a>
      </Message>
    )
    // makes the last "jump to next error" work... (let's do not make implementation any more complicate)
    tail_message = (
      <Message id={`error-msg-${err_msg_uid_cnt}`}>
        No further errors.{' '}
        <a href="#error-msg-0">
          Jump to First
          <Icon name="arrow alternate circle up outline" />
        </a>
      </Message>
    )
  }

  let menu = (
    <XMenu>
      <Menu.Item
        icon={{name: 'refresh', loading: refdb_updating}}
        onClick={updateRefDb}
        disabled={refdb_updating}
        content="Recompare"
      />
      <Menu.Item
        icon={{name: 'refresh', loading: desc_updating}}
        onClick={updateDescriptions}
        disabled={desc_updating}
        content="Update Descriptions"
      />
    </XMenu>
  )
  return (
    <HeaderSegment
      major
      header="Database Schema Overview"
      subheader="Tables, Views, etc."
      icon={databaseIcon('th list')}
      menu={menu}
    >
      {welcome_message}
      {schema_output}
      {tail_message}
    </HeaderSegment>
  )
}

export const DatabaseBackup = ({backupInfo: data, reloadPartFilters, callApi}) => {
  let [successNotification] = useActions(['successNotification'])
  let [selection, setSelection] = useState([])
  let startBackupJob = () => {
    callApi('startJob', {job: 'BackupDatabaseJob'}, () =>
      successNotification({
        header: 'Backup Job started',
        message: 'This can take a long time. Use refresh to check if it is done.',
        dismissAfter: 5000,
      })
    )
  }
  let backups_nongen = useMemo(() => data.backups.filter(f => !f.is_gen), [data])
  let backups_gen = useMemo(() => data.backups.filter(f => f.is_gen), [data])
  let render_file_table = files => {
    let tcolumns = [
      {
        Header: 'File',
        accessor: 'name',
        Cell: props => (
          <a href={`/debug/serveBackupFile/${props.value}`}>
            <Icon name="download" />
            {props.value}
          </a>
        ),
        minWidth: 280,
      },
      {
        Header: 'Finished',
        id: 'finished',
        accessor: 'time',
        Cell: props => formatDate(new Date(props.value * 1000), 'HH:MM:ss.SSS'),
        minWidth: 190,
      },
      {
        Header: 'Age',
        id: 'age',
        accessor: 'time',
        Cell: props => formatDistance(new Date(props.value * 1000), new Date()),
      },
      {
        Header: 'Size',
        accessor: 'size',
        Cell: props => prettyBytes(props.value),
      },
    ]
    let tdefault_sort = [
      {
        id: 'age',
        desc: true,
      },
    ]
    return (
      <SelectableTable
        filterable
        selection={selection}
        selectionKey="name"
        onSelectionChange={setSelection}
        compact
        data={files}
        columns={tcolumns}
        className="-striped -highlight dynHeight"
        showPagination={false}
        pageSize={files.length}
        defaultSorted={tdefault_sort}
      />
    )
  }
  let menu = (
    <XMenu>
      <Menu.Item
        icon="refresh"
        onClick={() => reloadPartFilters('DatabaseBackup')}
        title="Refresh View"
      ></Menu.Item>
      <Menu.Item icon="save" onClick={startBackupJob} name="Create New Backup" />
      <XMenu.Item
        icon="trash"
        title="Delete backups"
        name="Delete Selected Backups"
        disabled={selection.length < 1}
        confirm="The backups will be deleted, are you sure?"
        onClick={() => {
          setSelection([])
        }}
      ></XMenu.Item>
    </XMenu>
  )
  return (
    <HeaderSegment major header="Database Backups" icon={databaseIcon('save')} menu={menu}>
      <HeaderSegment
        basic
        header={'Backups'}
        subheader={`${backups_nongen.length} Items, Location: ${data.path}`}
      >
        {render_file_table(backups_nongen)}
      </HeaderSegment>
      <HeaderSegment
        basic
        header={'Generational Backups'}
        subheader={`${backups_gen.length} Items, Location: ${data.pathGenerations}`}
      >
        {render_file_table(backups_gen)}
      </HeaderSegment>
    </HeaderSegment>
  )
}

export const DatabaseUpdates = ({
  databaseInfo: db_info,
  versionInfo: update_history,
  databaseUpdates: updates_avail,
  reloadPartFilters,
  callApi,
}) => {
  const UPDATES_TPAGE_SIZE = 20

  let [successNotification] = useActions(['successNotification'])

  let current_version = db_info.version

  let startUpdateJob = ({to_version, backup = true}) => {
    return () =>
      callApi(
        'startJob',
        {
          job: 'UpdateDatabaseJob',
          // TODO: check if using current_version here is good, or passing as prop better
          fromVersion: current_version,
          toVersion: to_version,
          backupDatabase: backup,
        },
        () =>
          successNotification({
            header: 'Update Job started',
            message: 'This can take some. Use refresh to check if it is done.',
            dismissAfter: 5000,
          })
      )
  }

  let t_uhistory_cols = [
    {
      Header: 'Version',
      accessor: 'su_version',
      minWidth: 80,
    },
    {
      Header: 'From',
      accessor: 'su_date',
      minWidth: 80,
    },
    {
      Header: 'Update Finished',
      accessor: 'su_insertts',
      Cell: ({value}) => formatDate(value, 'yyyy-mm-dd HH:MM:ss'),
      minWidth: 120,
    },
    {
      Header: 'Comment',
      accessor: 'su_comment',
      minWidth: 200,
    },
  ]
  let t_uhistory_sort = [
    {
      id: 'su_insertts',
      desc: true,
    },
  ]
  let t_uavail_cols = [
    {
      Header: 'From',
      accessor: 'from',
      minWidth: 80,
    },
    {
      Header: 'To Version',
      accessor: 'to',
      minWidth: 80,
    },
    {
      Header: 'File',
      accessor: 'file',
      minWidth: 280,
    },
    {
      Header: null,
      accessor: null,
      Cell: ({original}) =>
        current_version >= original.to ? null : (
          <div style={{display: 'flex', justifyContent: 'center'}}>
            <Button
              basic
              compact
              attached="left"
              onClick={startUpdateJob({to_version: original.to})}
            >
              Update to {original.to}
            </Button>
            <Button
              basic
              compact
              attached="right"
              onClick={startUpdateJob({to_version: original.to, backup: false})}
            >
              <em>Without</em> Backup
            </Button>
          </div>
        ),
      minWidth: 260,
    },
  ]
  let t_uavail_sort = [
    {
      id: 'from',
      desc: true,
    },
    {
      id: 'to',
      desc: true,
    },
  ]
  let menu = (
    <XMenu>
      <Menu.Item
        icon="refresh"
        onClick={() => reloadPartFilters('DatabaseUpdates')}
        title="Refresh View"
      ></Menu.Item>
    </XMenu>
  )
  return (
    <HeaderSegment
      major
      header="Database Updates"
      subheader="History and Outstanding"
      icon={databaseIcon('history')}
      menu={menu}
    >
      <HeaderSegment basic header="History of past Updates">
        <TranslatedTable
          compact
          data={update_history['$children']}
          columns={t_uhistory_cols}
          className="-striped -highlight dynHeight"
          showPagination={update_history['$children'].length > UPDATES_TPAGE_SIZE}
          defaultPageSize={Math.min(UPDATES_TPAGE_SIZE, update_history['$children'].length + 1)}
          defaultSorted={t_uhistory_sort}
        />
      </HeaderSegment>
      <HeaderSegment basic header="Available Updates">
        <TranslatedTable
          compact
          data={updates_avail['$children']}
          columns={t_uavail_cols}
          className="-striped -highlight dynHeight"
          showPagination={updates_avail['$children'].length > UPDATES_TPAGE_SIZE}
          defaultPageSize={Math.min(UPDATES_TPAGE_SIZE, updates_avail['$children'].length + 1)}
          defaultSorted={t_uavail_sort}
        />
      </HeaderSegment>
    </HeaderSegment>
  )
}
